import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import CommunityLayout from "@/components/community/CommunityLayout";
import { EmptyState } from "@/components/community/Cards";
import { cApi, errMsg, fmtDate, fmtTime, img, useCommunityMe } from "@/lib/community";
import { startGoogleLogin, useCustomerAuth } from "@/context/CustomerAuthContext";

const PREF_LABELS = [
  ["bulletins", "Weekly bulletin emails"],
  ["event_updates", "New experience announcements"],
  ["community_updates", "Community updates & inspiration"],
];

export function PrefsEditor({ initial, save, testPrefix = "prefs" }) {
  const [p, setP] = useState(initial);
  useEffect(() => setP(initial), [initial]);
  const update = async (patch) => {
    const next = { ...p, ...patch };
    setP(next);
    try {
      await save(next);
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!p) return null;
  return (
    <div className="space-y-5" data-testid={`${testPrefix}-editor`}>
      {PREF_LABELS.map(([k, label]) => (
        <label key={k} className="flex items-center justify-between gap-4 text-sm">
          <span>{label}</span>
          <Switch checked={!p.unsubscribed_all && p[k] !== false} disabled={p.unsubscribed_all} onCheckedChange={(v) => update({ [k]: v })} data-testid={`${testPrefix}-${k}`} />
        </label>
      ))}
      <label className="flex items-center justify-between gap-4 text-sm border-t border-[#E8DCCB] pt-5">
        <span>Unsubscribe from all non-essential emails</span>
        <Switch checked={!!p.unsubscribed_all} onCheckedChange={(v) => update({ unsubscribed_all: v })} data-testid={`${testPrefix}-unsubscribed_all`} />
      </label>
      <p className="text-xs text-[#7A6A62]">You'll still receive essential emails such as RSVP confirmations, reminders, cancellations and membership decisions.</p>
    </div>
  );
}

const STATUS_COPY = {
  none: "Not applied yet", pending: "In review", approved: "Approved", rejected: "Not approved", suspended: "Suspended", revoked: "Revoked",
};

function Card({ title, children, testId }) {
  return (
    <div className="bg-[#FBF8F3] border border-[#E8DCCB] p-7 md:p-9 rounded-sm" data-testid={testId}>
      <p className="jc-eyebrow mb-6">{title}</p>
      {children}
    </div>
  );
}

export default function MyJenova() {
  const { user, me, loading, reload } = useCommunityMe();
  const { logout } = useCustomerAuth();
  const [rsvps, setRsvps] = useState([]);
  const [subs, setSubs] = useState([]);

  const loadLists = () => {
    cApi.get("/community/me/rsvps").then(({ data }) => setRsvps(data)).catch(() => {});
    cApi.get("/community/me/submissions").then(({ data }) => setSubs(data)).catch(() => {});
  };
  useEffect(() => {
    if (user) loadLists();
  }, [user]);

  const withdraw = async (id) => {
    await cApi.delete(`/community/me/submissions/${id}`).catch(() => {});
    loadLists();
  };

  if (loading) return <CommunityLayout><div className="h-96" /></CommunityLayout>;
  if (!user)
    return (
      <CommunityLayout>
        <div className="max-w-xl mx-auto px-6 py-32 text-center" data-testid="my-jenova-signin">
          <h1 className="jc-display text-5xl">My <em className="italic text-[#A8865A]">Jenova</em></h1>
          <p className="mt-6 text-[#7A6A62]">Sign in to see your membership, RSVPs and email preferences.</p>
          <button onClick={() => startGoogleLogin("/my-jenova")} className="jc-btn jc-btn-primary mt-10" data-testid="my-jenova-signin-btn">Sign in with Google</button>
        </div>
      </CommunityLayout>
    );

  const m = me?.membership || {};
  return (
    <CommunityLayout>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24" data-testid="my-jenova">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="jc-eyebrow">My Jenova</p>
            <h1 className="jc-display text-5xl md:text-6xl mt-4">Hello, <em className="italic text-[#A8865A]">{(m.name || user.name || "sister").split(" ")[0]}</em></h1>
            <p className="mt-3 text-sm text-[#7A6A62]">{user.email}</p>
          </div>
          <button onClick={logout} className="jc-btn jc-btn-ghost" data-testid="my-jenova-logout-btn">Sign out</button>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          <Card title="Community membership" testId="my-community-status">
            <p className="jc-display text-3xl">{STATUS_COPY[m.community_status || "none"]}</p>
            {(!m.community_status || m.community_status === "none" || m.community_status === "rejected") && (
              <Link to="/become-a-jenova-girl" className="jc-btn jc-btn-blush mt-6" data-testid="my-apply-btn">Become a Jenova Girl</Link>
            )}
          </Card>
          <Card title="Privé Club" testId="my-prive-status">
            <p className="jc-display text-3xl">{STATUS_COPY[m.prive_status || "none"]}</p>
            <Link to="/prive" className="jc-btn jc-btn-ghost mt-6" data-testid="my-prive-btn">{m.prive_status === "approved" ? "Enter Privé" : "Learn about Privé"}</Link>
          </Card>
          <Card title="Email preferences" testId="my-prefs">
            <PrefsEditor initial={me?.preferences} save={(p) => cApi.put("/community/me/preferences", p).then(reload)} testPrefix="my-prefs" />
          </Card>
        </div>
        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <Card title="My experiences" testId="my-rsvps">
            {rsvps.length ? (
              <ul className="divide-y divide-[#E8DCCB]">
                {rsvps.map((r) => (
                  <li key={r.id} className="py-4 flex items-center justify-between gap-4">
                    <div>
                      <Link to={`/events/${r.event_id}`} className="font-serif text-xl hover:text-[#A8865A]">{r.event.title}</Link>
                      <p className="text-xs text-[#7A6A62] mt-1">{fmtDate(r.event.start_at)} · {fmtTime(r.event.start_at)}</p>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#A8865A]">{r.event.status === "cancelled" ? "Cancelled" : r.status}</span>
                  </li>
                ))}
              </ul>
            ) : <EmptyState title="No RSVPs yet"><Link to="/events" className="underline">Explore experiences</Link></EmptyState>}
          </Card>
          <Card title="My photo submissions" testId="my-submissions">
            {subs.length ? (
              <div className="grid grid-cols-3 gap-3">
                {subs.map((p) => (
                  <div key={p.id} className="relative">
                    <div className="jc-card-img aspect-square"><img src={img(p.url)} alt="" className="w-full h-full object-cover" /></div>
                    <p className="text-[10px] uppercase tracking-[0.2em] mt-2 text-[#7A6A62]">{p.status}</p>
                    <button className="text-[10px] underline text-[#7A6A62]" onClick={() => withdraw(p.id)} data-testid={`my-withdraw-${p.id}`}>Withdraw</button>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-[#7A6A62]">Photos you share on the Community Wall appear here.</p>}
          </Card>
        </div>
      </div>
    </CommunityLayout>
  );
}

export function TokenPreferences() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!token) return setErr("This link is missing its token");
    cApi.get(`/community/preferences/${token}`).then(({ data }) => setP(data)).catch((e) => setErr(errMsg(e)));
  }, [token]);
  return (
    <CommunityLayout>
      <div className="max-w-lg mx-auto px-6 py-24" data-testid="token-preferences">
        <p className="jc-eyebrow">Email preferences</p>
        <h1 className="jc-display text-5xl mt-4">Your inbox, <em className="italic text-[#A8865A]">your way</em></h1>
        {p && <p className="mt-4 text-sm text-[#7A6A62]">{p.email}</p>}
        <div className="mt-12">
          {err ? <EmptyState title={err} /> : <PrefsEditor initial={p} save={(x) => cApi.put(`/community/preferences/${token}`, x)} testPrefix="token-prefs" />}
        </div>
      </div>
    </CommunityLayout>
  );
}

export function Guidelines() {
  return (
    <CommunityLayout>
      <article className="max-w-2xl mx-auto px-6 py-20 text-[#4A403B] leading-[1.9]" data-testid="guidelines-page">
        <p className="jc-eyebrow">The Jenova Girls Experience</p>
        <h1 className="jc-display text-5xl mt-4 text-[#2B2522]">Community guidelines</h1>
        <ul className="mt-10 list-disc pl-5 space-y-3">
          <li>Lead with kindness. Celebrate one another and speak life.</li>
          <li>Respect privacy. Never share another woman's photo, story or details without her consent.</li>
          <li>Show up. If you RSVP and can't make it, cancel so another sister can take your spot.</li>
          <li>No harassment, discrimination, solicitation or spam — online or at experiences.</li>
          <li>Faith conversations are offered with love; everyone is welcome to listen and share at her own pace.</li>
          <li>Jenova may suspend membership when these guidelines are not honoured.</li>
        </ul>
        <h2 id="privacy" className="jc-display text-4xl mt-16 text-[#2B2522]">Privacy</h2>
        <p className="mt-6">We collect the details you share in your application (name, email, city, state, optional Instagram, introduction and interests) to review your membership and invite you to experiences. We never sell your information.</p>
        <p className="mt-4">Purchase details used for Privé verification are visible only to Jenova administrators. Photos you submit are reviewed before publishing and can be withdrawn anytime from My Jenova.</p>
        <p className="mt-4">Marketing emails can be managed or unsubscribed anytime; essential emails (RSVP confirmations, reminders, cancellations and membership decisions) are always sent. To delete your data, <Link to="/contact" className="underline">contact us</Link>.</p>
      </article>
    </CommunityLayout>
  );
}
